import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  IconButton,
  Chip,
  TextField,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondary,
  ListItemAvatar,
  ListItemIcon,
  Drawer,
  useTheme,
  useMediaQuery,
  Divider,
  Fab,
  RadioGroup,
  FormControlLabel,
  Radio,
  Menu,
  DialogContentText,
} from '@mui/material';
import RentalCard from '../../components/rental/RentalCard';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as FavoriteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  PostAdd as PostAddIcon,
  Bookmark as BookmarkIcon,
  Timeline as TimelineIcon,
  Menu as MenuIcon,
  Add as AddIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { getCurrentUser } from '../../utils/auth';
import { roommateAPI } from '../../utils/api';
import { authAPI } from '../../utils/api';
import { userAPI } from '../../utils/api';
import { rentalDetailPath, roommateDetailPath } from '../../utils/listingPaths';

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ mt: 3 }}>
      {value === index && children}
    </Box>
  );
}

function UserProfile() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [user, setUser] = useState(getCurrentUser());
  const [editMode, setEditMode] = useState(false);
  const [editedUser, setEditedUser] = useState(user);
  const [statusDialog, setStatusDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [newPostAnchorEl, setNewPostAnchorEl] = useState(null);
  const [roommatePosts, setRoommatePosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [progresses, setProgresses] = useState([]);
  const [moveInDateDialog, setMoveInDateDialog] = useState(false);
  const [selectedProgress, setSelectedProgress] = useState(null);
  const [moveInDate, setMoveInDate] = useState('');
  const [declineDialog, setDeclineDialog] = useState(false);
  const [postLimitDialog, setPostLimitDialog] = useState(false);

  const drawerWidth = 240;

  const menuItems = [
    { text: 'Profile', icon: <PersonIcon />, index: 0 },
    { text: 'My Posts', icon: <PostAddIcon />, index: 1 },
    { text: 'Favorites', icon: <BookmarkIcon />, index: 2 },
    { text: 'In Progress', icon: <TimelineIcon />, index: 3 },
  ];

  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setSelectedTab(location.state.activeTab);
      // 清除state，防止用户刷新页面时重复设置标签
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await authAPI.getProfile();
        const userData = response.data;
        setUser(userData);
        setEditedUser(userData);
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        // 如果获取失败，使用本地存储的用户数据
        const localUser = getCurrentUser();
        if (localUser) {
          setUser(localUser);
          setEditedUser(localUser);
        }
      }
    };

    fetchUserProfile();
  }, []);

  // 加载室友帖子
  useEffect(() => {
    const fetchRoommatePosts = async () => {
      setLoading(true);
      try {
        const res = await roommateAPI.getMyRoommatePosts();
        setRoommatePosts(res.data.results || res.data);
      } catch (error) {
        console.error('Failed to fetch roommate posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRoommatePosts();
  }, []);

  useEffect(() => {
    userAPI.getFavorites().then(res => setFavorites(res.data));
  }, []);

  // 加载Progress数据
  useEffect(() => {
    const fetchProgresses = async () => {
      try {
        const res = await userAPI.getProgress();
        setProgresses(res.data);
      } catch (error) {
        console.error('Failed to fetch progresses:', error);
      }
    };
    fetchProgresses();
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleEditProfile = () => {
    setEditMode(true);
  };

  const handleSaveProfile = async () => {
    try {
      // 准备要发送的数据
      const formData = new FormData();
      formData.append('first_name', editedUser.first_name || editedUser.name?.split(' ')[0] || '');
      formData.append('last_name', editedUser.last_name || editedUser.name?.split(' ').slice(1).join(' ') || '');
      formData.append('phone', editedUser.phone || '');
      
      // 如果有新的头像文件，添加到formData
      if (editedUser.profile_image instanceof File) {
        formData.append('profile_image', editedUser.profile_image);
      }
      
      // 调用后端API保存用户资料
      const response = await authAPI.updateProfile(formData);
      
      // 更新本地用户数据
      const updatedUser = { ...user, ...response.data };
      setUser(updatedUser);
      setEditMode(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStatusUpdate = (property) => {
    setSelectedProperty(property);
    setStatusDialog(true);
  };

  const handleStatusChange = async (status) => {
    if (status === 'signed') {
      // 如果状态改为signed，询问入住日期
      setSelectedProgress(selectedProperty);
      setMoveInDateDialog(true);
      setStatusDialog(false);
    } else {
      // 其他状态直接更新
      try {
        await userAPI.updateProgress(selectedProperty.id, { status });
        // 更新本地状态
        setProgresses(prev => prev.map(p => 
          p.id === selectedProperty.id ? { ...p, status } : p
        ));
        setStatusDialog(false);
        alert('Status updated successfully!');
      } catch (error) {
        console.error('Failed to update status:', error);
        alert('Failed to update status. Please try again.');
      }
    }
  };

  const handleMoveInDateSubmit = async () => {
    if (!moveInDate) {
      alert('Please select a move-in date');
      return;
    }
    
    try {
      await userAPI.updateProgress(selectedProgress.id, { 
        status: 'signed',
        move_in_date: moveInDate 
      });
      
      // 更新本地状态
      setProgresses(prev => prev.map(p => 
        p.id === selectedProgress.id ? { ...p, status: 'signed', move_in_date: moveInDate } : p
      ));
      
      setMoveInDateDialog(false);
      setMoveInDate('');
      setSelectedProgress(null);
      alert('Status updated successfully!');
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      contacted: { label: 'Contacted', color: 'info' },
      signed: { label: 'Signed', color: 'success' },
    };
    const config = statusConfig[status] || statusConfig.contacted;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleNewPostClick = (event) => {
    setNewPostAnchorEl(event.currentTarget);
  };

  const handleNewPostClose = () => {
    setNewPostAnchorEl(null);
  };

  const handleNewPostType = (type) => {
    if (type === 'roommate') {
      // 检查是否已达到帖子数量限制
      if (roommatePosts.length >= 2) {
        setPostLimitDialog(true);
        setNewPostAnchorEl(null);
        return;
      }
      navigate('/posts/roommate/new');
    }
    setNewPostAnchorEl(null);
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await roommateAPI.deleteRoommatePost(postId);
        // 从本地状态中移除被删除的帖子
        setRoommatePosts(prev => prev.filter(post => post.id !== postId));
      } catch (error) {
        console.error('Failed to delete post:', error);
        alert('Failed to delete post. Please try again.');
      }
    }
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditedUser(prev => ({
          ...prev,
          profile_image: file,
          avatar: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeclineProgress = async () => {
    if (!selectedProgress) return;
    try {
      await userAPI.updateProgress(selectedProgress.id, { status: 'declined' });
      setProgresses(prev => prev.filter(p => p.id !== selectedProgress.id));
      setDeclineDialog(false);
      setSelectedProgress(null);
      alert('Progress declined and removed.');
    } catch (error) {
      alert('Failed to decline. Please try again.');
    }
  };

  const handlePostLimitDialogClose = () => {
    setPostLimitDialog(false);
  };

  const drawer = (
    <Box sx={{ width: drawerWidth }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Avatar
          sx={{ width: 80, height: 80, mx: 'auto', mb: 1 }}
          src={user.profile_image_url || user.profile_image || user.avatar}
        >
          {user.name?.[0] || user.first_name?.[0] || user.email?.[0]}
        </Avatar>
        <Typography variant="h6" noWrap>
          {user.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {user.email}
        </Typography>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            selected={selectedTab === item.index}
            onClick={() => {
              setSelectedTab(item.index);
              if (isMobile) {
                setMobileDrawerOpen(false);
              }
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderContent = () => {
    switch (selectedTab) {
      case 0:
        return (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              sx={{ position: 'absolute', top: 0, right: 0 }}
              onClick={handleEditProfile}
              disabled={editMode}
            >
              <EditIcon />
            </IconButton>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                    src={user.profile_image_url || user.profile_image || user.avatar}
                  >
                    {user.name?.[0] || user.first_name?.[0] || user.email?.[0]}
                  </Avatar>
                  {editMode && (
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        '&:hover': { bgcolor: 'background.paper' },
                      }}
                      onClick={() => document.getElementById('avatar-upload').click()}
                    >
                      <PhotoCameraIcon />
                    </IconButton>
                  )}
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleAvatarUpload}
                  />
                </Box>
                {!editMode ? (
                  <Typography variant="h5">{`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'No name set'}</Typography>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="first_name"
                      value={editedUser.first_name || ''}
                      onChange={handleInputChange}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="last_name"
                      value={editedUser.last_name || ''}
                      onChange={handleInputChange}
                      sx={{ mb: 2 }}
                    />
                  </>
                )}
              </Grid>

              <Grid item xs={12} md={8}>
                <List>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <EmailIcon />
                      </Avatar>
                    </ListItemAvatar>
                    {!editMode ? (
                      <ListItemText primary="Email" secondary={user.email} />
                    ) : (
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={editedUser.email}
                        onChange={handleInputChange}
                      />
                    )}
                  </ListItem>

                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <PhoneIcon />
                      </Avatar>
                    </ListItemAvatar>
                    {!editMode ? (
                      <ListItemText primary="Phone" secondary={user.phone || 'Not set'} />
                    ) : (
                      <TextField
                        fullWidth
                        label="Phone"
                        name="phone"
                        value={editedUser.phone || ''}
                        onChange={handleInputChange}
                      />
                    )}
                  </ListItem>
                </List>

                {editMode && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button onClick={() => setEditMode(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveProfile}>
                      Save
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewPostClick}
              >
                New Post
              </Button>
              <Menu
                anchorEl={newPostAnchorEl}
                open={Boolean(newPostAnchorEl)}
                onClose={handleNewPostClose}
              >
                <MenuItem onClick={() => handleNewPostType('roommate')}>
                  Roommate Post
                </MenuItem>
              </Menu>
            </Box>
            <Grid container spacing={4}>
              {loading ? (
                <Grid item xs={12}>
                  <Typography>Loading posts...</Typography>
                </Grid>
              ) : roommatePosts.length === 0 ? (
                <Grid item xs={12}>
                  <Typography color="text.secondary">No posts yet. Create your first post!</Typography>
                </Grid>
              ) : (
                roommatePosts.map((post) => (
                  <Grid item xs={12} md={6} key={post.id}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                      onClick={() => navigate(roommateDetailPath(post))}
                    >
                      <CardMedia
                        component="img"
                        height="240"
                        image={post.images && post.images.length > 0 ? post.images[0] : 'https://source.unsplash.com/random/800x600/?apartment'}
                        alt={post.title}
                      />
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ minHeight: '2em', fontSize: '1.2rem' }}>
                          {post.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {post.city}, {post.state} • ${post.price}/month
                        </Typography>
                        <Box sx={{ flexGrow: 1, mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Posted: {new Date(post.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'flex-end', mt: 'auto', p: 2 }}>
                        <Button 
                          size="medium" 
                          startIcon={<EditIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/posts/roommate/${post.id}/edit`);
                          }}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="medium" 
                          color="error" 
                          startIcon={<DeleteIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePost(post.id);
                          }}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </>
        );
      case 2:
        return (
          <Box>
            {favorites.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No favorites yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start browsing properties and add them to your favorites!
                </Typography>
              </Box>
            ) : (
              <Box>
                {favorites.map((rental) => (
                  <Card
                    key={rental.id}
                    sx={{
                      mb: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onClick={() => navigate(rentalDetailPath(rental))}
                  >
                    {/* 左侧内容：标题和地址 */}
                    <Box
                      sx={{
                        flex: 1,
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          {rental.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {rental.address_line1}
                          {rental.address_line2 ? `, ${rental.address_line2}` : ''}
                          {rental.city ? `, ${rental.city}` : ''}
                          {rental.state ? `, ${rental.state}` : ''}
                          {rental.zip_code ? ` ${rental.zip_code}` : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Added to favorites on {new Date(rental.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* 右侧图片 */}
                    <Box
                      sx={{
                        width: { xs: '100%', sm: 200 },
                        height: { xs: 200, sm: 'auto' },
                        minHeight: { sm: 150 },
                        flexShrink: 0,
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="100%"
                        image={rental.images && rental.images.length > 0 ? rental.images[0] : '/default-property.jpg'}
                        alt={rental.title}
                        sx={{
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        );
      case 3:
        return (
          <Grid container spacing={4}>
            {progresses.length === 0 ? (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No properties in progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    When you message landlords about properties, they will appear here.
                  </Typography>
                </Box>
              </Grid>
            ) : (
              progresses.map((progress) => (
                <Grid item xs={12} md={6} key={progress.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onClick={() => navigate(rentalDetailPath(progress.rental))}
                  >
                    <CardMedia
                      component="img"
                      height="240"
                      image={progress.rental?.images?.[0] || '/default-property.jpg'}
                      alt={progress.rental?.title || 'Property'}
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ minHeight: '2em', fontSize: '1.2rem' }}>
                          {progress.rental?.title || 'Property Title'}
                        </Typography>
                        {getStatusChip(progress.status)}
                      </Box>
                      {progress.move_in_date && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Move-in Date: {new Date(progress.move_in_date).toLocaleDateString()}
                        </Typography>
                      )}
                      {progress.status === 'signed' && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Landlord Confirmation:
                          </Typography>
                          {progress.landlord_confirmed ? (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'success.main', 
                                fontWeight: 600
                              }}
                            >
                              Landlord confirmed
                              {progress.landlord_confirmed_at && (
                                <Typography variant="caption" color="text.secondary">
                                  ({new Date(progress.landlord_confirmed_at).toLocaleDateString()})
                                </Typography>
                              )}
                            </Typography>
                          ) : (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'warning.main', 
                                fontWeight: 600
                              }}
                            >
                              Waiting for landlord confirmation
                            </Typography>
                          )}
                        </Box>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Last Updated: {new Date(progress.updated_at).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', mt: 'auto', p: 2 }}>
                      {progress.status === 'contacted' && (
                        <>
                          <Button
                            size="medium"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProgress(progress);
                              setMoveInDateDialog(true);
                            }}
                          >
                            Sign Lease
                          </Button>
                          <Button
                            size="medium"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProgress(progress);
                              setDeclineDialog(true);
                            }}
                          >
                            Not going to sign
                          </Button>
                        </>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* 左侧导航栏 */}
      <Box
        sx={{
          width: 240,
          flexShrink: 0,
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          position: 'sticky',
          top: 64, // 顶部导航栏的高度
          height: 'calc(100vh - 64px)', // 减去顶部导航栏的高度
          overflowY: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Avatar
            sx={{ width: 80, height: 80, mx: 'auto', mb: 1 }}
            src={user.profile_image_url || user.profile_image || user.avatar}
          >
            {user.name?.[0] || user.first_name?.[0] || user.email?.[0]}
          </Avatar>
          <Typography variant="h6" noWrap>
            {user.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user.email}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <List>
          {menuItems.map((item) => (
            <ListItem
              button
              key={item.text}
              selected={selectedTab === item.index}
              onClick={() => setSelectedTab(item.index)}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: selectedTab === item.index ? 'inherit' : 'primary.main',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* 右侧内容区域 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          bgcolor: 'background.default',
          minHeight: '100%',
        }}
      >
        <Typography variant="h5" gutterBottom>
          {menuItems[selectedTab].text}
        </Typography>
        {renderContent()}
      </Box>

      {/* 入住日期选择对话框 */}
      <Dialog open={moveInDateDialog} onClose={() => setMoveInDateDialog(false)}>
        <DialogTitle>Select Move-in Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please select your move-in date for {selectedProgress?.rental?.title || 'this property'}.
          </Typography>
          <TextField
            fullWidth
            type="date"
            label="Move-in Date"
            value={moveInDate}
            onChange={(e) => setMoveInDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveInDateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMoveInDateSubmit}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline确认对话框 */}
      <Dialog open={declineDialog} onClose={() => setDeclineDialog(false)}>
        <DialogTitle>Not going to sign?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you don't want to sign for this property?<br /><br />
            After confirming, this property will be removed from your In Progress list.<br /><br />
            If you change your mind in the future, you can always message the property again to add it back.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialog(false)}>Cancel</Button>
          <Button onClick={handleDeclineProgress}>Not going to sign</Button>
        </DialogActions>
      </Dialog>

      {/* 帖子数量限制对话框 */}
      <Dialog
        open={postLimitDialog}
        onClose={handlePostLimitDialogClose}
        aria-labelledby="post-limit-dialog-title"
        aria-describedby="post-limit-dialog-description"
      >
        <DialogTitle id="post-limit-dialog-title">Post Limit Reached</DialogTitle>
        <DialogContent>
          <DialogContentText id="post-limit-dialog-description">
            You have reached the maximum limit of 2 roommate posts. Please delete an existing post before creating a new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePostLimitDialogClose} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserProfile; 